import * as React from "react";

import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    DraggableProvidedDragHandleProps,
    Droppable,
} from "@hello-pangea/dnd";
import {PhotoIcon} from "@heroicons/react/24/outline";
import {Decimal} from "decimal.js";
import {AnimatePresence, motion} from "framer-motion";

import {resolveEntry} from "@client/features/camera";
import * as dialogs from "@client/features/dialogs";
import type {ActivityRow} from "@client/features/journal";
import {LATE_MEAL_HOUR, hourToName} from "@client/features/journal";

import * as constants from "@client/constants";

import {
    calculateEntryMetrics,
    calculateMealMetrics,
    getRoundedCalculations,
    getRoundedQuantity,
} from "@client/calculations";
import {Icon} from "@client/components/Icon";
import {TouchHandler} from "@client/components/TouchHandler";
import {Locator} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";
import * as forms from "@client/site/forms";
import {useAppDispatch, useAppSelector} from "@client/store";
import {classNames, server} from "@reactivated";

interface Props {
    meal: server.journal.nutrition.Meal.output;

    // Make this empty for public meals, etc.
    links?: {
        mealLink: Locator;
        getEntryLink: {
            hack(entry: server.journal.nutrition.Entry.output): string | Locator;
        }["hack"];
    };
    sorting?: {
        isDragging: boolean;
        handle: DraggableProvidedDragHandleProps | null;
    };
    hiddenMacros: boolean;
    defaultExpanded?: boolean;
}

interface EntryCardProps {
    entry: server.journal.nutrition.ExpandedEntry.output;
    index: number;
    provided: DraggableProvided;
    entryLink?: string | Locator;
}

const level2Class = "text-xs font-normal text-gray-500";

function EntryMetrics(props: {
    calculations: ReturnType<typeof getRoundedCalculations>;
}) {
    const {calculations} = props;

    return (
        <div
            className={classNames(
                "flex gap-1 tabular-nums tracking-tight *:truncate",
                level2Class,
            )}
        >
            <span className="truncate">
                P {calculations.protein}g<span className="px-1.5 text-gray-300">•</span>
                F {calculations.fat}g<span className="px-1.5 text-gray-300">•</span>C{" "}
                {calculations.carbs}g
            </span>
        </div>
    );
}

const Broccoli = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        id="Vegetables-Broccoli-1--Streamline-Ultimate"
        height={24}
        width={24}
    >
        <desc>
            {
                "\n    Vegetables Broccoli 1 Streamline Icon: https://streamlinehq.com\n  "
            }
        </desc>
        <path
            fill="#78eb7b"
            d="m14.005 19.024 0.39 0.39c0.5133 0.5126 1.1258 0.9151 1.7999 1.183 0.6742 0.2678 1.3959 0.3954 2.1211 0.3748 0.7251 -0.0205 1.4384 -0.1888 2.0963 -0.4944 0.6579 -0.3056 1.2466 -0.7422 1.7301 -1.283 0.4834 -0.5409 0.8515 -1.1746 1.0817 -1.8626 0.2302 -0.6879 0.3176 -1.4156 0.2571 -2.1385 -0.0606 -0.7229 -0.268 -1.4258 -0.6095 -2.0658 -0.3415 -0.64 -0.81 -1.2037 -1.3767 -1.6565l-0.014 -0.014c0.3756 -1.2397 0.4077 -2.55804 0.093 -3.81452 -0.3148 -1.25649 -0.9646 -2.404 -1.8803 -3.32018 -0.9157 -0.91619 -2.0628 -1.56669 -3.3191 -1.88216 -1.2564 -0.31547 -2.5747 -0.28407 -3.8146 0.09084l-0.02 -0.02c-0.4518 -0.5667 -1.0143 -1.03542 -1.6532 -1.37753 -0.639 -0.34212 -1.34089 -0.550467 -2.063 -0.612331 -0.72211 -0.061863 -1.44926 0.024053 -2.13706 0.252507 -0.68781 0.228454 -1.32186 0.594654 -1.86346 1.076254 -0.5416 0.48161 -0.97939 1.06851 -1.28666 1.7249 -0.30727 0.6564 -0.47758 1.36852 -0.50053 2.09291 -0.02296 0.72439 0.10193 1.44586 0.36703 2.12039 0.2651 0.67454 0.66486 1.28798 1.17488 1.8029l0.4 0.4 9.027 9.03302Z"
            strokeWidth={1}
        />
        <path
            fill="#c9f7ca"
            d="M5.532 6.72398c0.28788 -0.92601 0.82275 -1.75603 1.54709 -2.40078 0.72434 -0.64475 1.61074 -1.07984 2.56387 -1.25847 0.95314 -0.17864 1.93694 -0.09406 2.84554 0.24464 0.9087 0.33869 1.7078 0.91869 2.3115 1.67761l0.02 0.02c1.0218 -0.30288 2.0982 -0.37514 3.1513 -0.21154 1.0532 0.16359 2.0569 0.55895 2.9387 1.15754 -0.7831 -1.44641 -2.0424 -2.57741 -3.5643 -3.20113 -1.5219 -0.62372 -3.2128 -0.70175 -4.7857 -0.22087l-0.02 -0.02c-0.4518 -0.5667 -1.0143 -1.03542 -1.6532 -1.37753 -0.639 -0.34212 -1.34089 -0.550467 -2.063 -0.612331 -0.72211 -0.061863 -1.44926 0.024053 -2.13706 0.252507 -0.68781 0.228454 -1.32186 0.594654 -1.86346 1.076254 -0.5416 0.48161 -0.97939 1.06851 -1.28666 1.7249 -0.30727 0.6564 -0.47758 1.36852 -0.50053 2.09291 -0.02296 0.72439 0.10193 1.44586 0.36703 2.12039 0.2651 0.67454 0.66486 1.28798 1.17488 1.8029l0.4 0.4L6.091 11.1c-0.40076 -0.647 -0.65804 -1.37243 -0.75447 -2.12734 -0.09643 -0.75491 -0.02978 -1.52173 0.19547 -2.24868Z"
            strokeWidth={1}
        />
        <path
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m14.005 19.024 0.39 0.39c0.5133 0.5126 1.1258 0.9151 1.7999 1.183 0.6742 0.2678 1.3959 0.3954 2.1211 0.3748 0.7251 -0.0205 1.4384 -0.1888 2.0963 -0.4944 0.6579 -0.3056 1.2466 -0.7422 1.7301 -1.283 0.4834 -0.5409 0.8515 -1.1746 1.0817 -1.8626 0.2302 -0.6879 0.3176 -1.4156 0.2571 -2.1385 -0.0606 -0.7229 -0.268 -1.4258 -0.6095 -2.0658 -0.3415 -0.64 -0.81 -1.2037 -1.3767 -1.6565l-0.014 -0.014c0.3756 -1.2397 0.4077 -2.55804 0.093 -3.81452 -0.3148 -1.25649 -0.9646 -2.404 -1.8803 -3.32018 -0.9157 -0.91619 -2.0628 -1.56669 -3.3191 -1.88216 -1.2564 -0.31547 -2.5747 -0.28407 -3.8146 0.09084l-0.02 -0.02c-0.4518 -0.5667 -1.0143 -1.03542 -1.6532 -1.37753 -0.639 -0.34212 -1.34089 -0.550467 -2.063 -0.612331 -0.72211 -0.061863 -1.44926 0.024053 -2.13706 0.252507 -0.68781 0.228454 -1.32186 0.594654 -1.86346 1.076254 -0.5416 0.48161 -0.97939 1.06851 -1.28666 1.7249 -0.30727 0.6564 -0.47758 1.36852 -0.50053 2.09291 -0.02296 0.72439 0.10193 1.44586 0.36703 2.12039 0.2651 0.67454 0.66486 1.28798 1.17488 1.8029l0.4 0.4 9.027 9.03302Z"
            strokeWidth={1}
        />
        <path
            fill="#78eb7b"
            d="M16.291 17.524H13.22c-0.7007 0 -1.3946 0.138 -2.042 0.4062 -0.6474 0.2682 -1.23556 0.6613 -1.731 1.1568l-4.1 4.1c-0.09901 0.0992 -0.21662 0.1779 -0.34609 0.2316 -0.12946 0.0537 -0.26825 0.0814 -0.40841 0.0814s-0.27895 -0.0277 -0.40841 -0.0814c-0.12947 -0.0537 -0.24708 -0.1324 -0.34609 -0.2316L0.816 20.174c-0.099204 -0.099 -0.177909 -0.2166 -0.231608 -0.3461 -0.0537 -0.1294 -0.081341 -0.2682 -0.081341 -0.4084s0.027641 -0.2789 0.081341 -0.4084c0.053699 -0.1295 0.132404 -0.2471 0.231608 -0.3461l4.1 -4.1c0.49559 -0.4954 0.88871 -1.0836 1.15689 -1.731 0.26819 -0.6474 0.40619 -1.3413 0.40611 -2.042V7.72101l3.018 0.46V12.25l2.263 -2.26399 2.264 2.26499 -2.264 2.263h4.069l0.462 3.01Z"
            strokeWidth={1}
        />
        <path
            fill="#c9f7ca"
            d="m2.207 20.383 4.744 -4.744c0.5733 -0.5731 1.02806 -1.2536 1.33828 -2.0026 0.31022 -0.749 0.46984 -1.5517 0.46972 -2.3624V8.07401l-2.28 -0.348V10.797c0.00008 0.7008 -0.13792 1.3946 -0.40611 2.042 -0.26818 0.6474 -0.6613 1.2356 -1.15689 1.731l-4.1 4.1c-0.099204 0.099 -0.177909 0.2166 -0.231608 0.3461 -0.0537 0.1295 -0.081341 0.2683 -0.081341 0.4084 0 0.1402 0.027641 0.279 0.081341 0.4084 0.053699 0.1295 0.132404 0.2471 0.231608 0.3461l1.038 1.031c0.00908 -0.3103 0.13518 -0.6058 0.353 -0.827Z"
            strokeWidth={1}
        />
        <path
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m14.024 12.251 -2.264 2.263h4.069"
            strokeWidth={1}
        />
        <path
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.5 8.18103V12.25l2.26 -2.26397"
            strokeWidth={1}
        />
        <path
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.291 17.524H13.22c-0.7007 0 -1.3946 0.138 -2.042 0.4062 -0.6474 0.2682 -1.23556 0.6613 -1.731 1.1568l-4.1 4.1c-0.09901 0.0992 -0.21662 0.1779 -0.34609 0.2316 -0.12946 0.0537 -0.26825 0.0814 -0.40841 0.0814s-0.27895 -0.0277 -0.40841 -0.0814c-0.12947 -0.0537 -0.24708 -0.1324 -0.34609 -0.2316L0.816 20.174c-0.099204 -0.099 -0.177909 -0.2166 -0.231608 -0.3461 -0.0537 -0.1294 -0.081341 -0.2682 -0.081341 -0.4084s0.027641 -0.2789 0.081341 -0.4084c0.053699 -0.1295 0.132404 -0.2471 0.231608 -0.3461l4.1 -4.1c0.49559 -0.4954 0.88871 -1.0836 1.15689 -1.731 0.26819 -0.6474 0.40619 -1.3413 0.40611 -2.042V7.72101"
            strokeWidth={1}
        />
        <path
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.978 3.18898c1.3682 -0.79656 2.9609 -1.11993 4.5314 -0.92007 1.5706 0.19987 3.0315 0.91182 4.1566 2.02567 1.1251 1.11385 1.8517 2.56749 2.0674 4.13594 0.2157 1.56846 -0.0917 3.16428 -0.8744 4.54048"
            strokeWidth={1}
        />
    </svg>
);
const Chicken = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="-0.5 -0.98 24 24"
        id="Animal-Products-Drumsticks--Streamline-Ultimate"
        height={24}
        width={24}
    >
        <desc>
            {
                "\n    Animal Products Drumsticks Streamline Icon: https://streamlinehq.com\n  "
            }
        </desc>
        <path
            fill="#ffffff"
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5.355319999999999 20.883999999999997a3.07004 3.07004 0 0 1 -0.61916 -2.5713999999999997 3.1546799999999995 3.1546799999999995 0 0 1 -2.62016 -0.6642399999999999c-2.29356 -2.29356 1.2705199999999999 -5.85764 3.7499199999999995 -2.484l2.7738 -2.8271599999999997 1.9522399999999998 1.95132L7.819999999999999 17.11752c3.3947999999999996 2.45916 -0.1702 6.066479999999999 -2.4646799999999995 3.76648Z"
            strokeWidth={1}
        />
        <path
            fill="#c77f67"
            d="m11.2884 14.98312 -3.2521999999999998 -3.25404c-1.73972 -1.7387999999999997 5.9201999999999995 -16.263759999999998 12.70796 -9.475999999999999 6.8355999999999995 6.83376 -7.93776 14.247119999999999 -9.45576 12.730039999999999Z"
            strokeWidth={1}
        />
        <path
            fill="#e3bfb3"
            d="M22.23364 5.888c0.10947999999999998 0.10947999999999998 0.19871999999999998 0.21987999999999996 0.29716 0.32936a5.28724 5.28724 0 0 0 -1.7866399999999998 -3.96428c-6.78776 -6.78776 -14.443999999999999 7.7372 -12.70796 9.475999999999999l1.45728 1.4582C10.69868 8.831999999999999 16.749519999999997 0.40571999999999997 22.23364 5.888Z"
            strokeWidth={1}
        />
        <path
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m11.2884 14.98496 -3.2521999999999998 -3.25404c-1.73972 -1.7387999999999997 5.9201999999999995 -16.263759999999998 12.70796 -9.475999999999999 6.8355999999999995 6.83376 -7.93776 14.247119999999999 -9.45576 12.730039999999999Z"
            strokeWidth={1}
        />
    </svg>
);
const Rice = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        id="Asian-Food-Rice--Streamline-Ultimate"
        height={24}
        width={24}
    >
        <desc>
            {"\n    Asian Food Rice Streamline Icon: https://streamlinehq.com\n  "}
        </desc>
        <path
            fill="#e3e3e3"
            d="M21.429 9.68A2.5 2.5 0 0 1 23 12v0.5H1.5a4 4 0 0 1 4 -4 3.531 3.531 0 0 1 1.552 0.312A4.5 4.5 0 0 1 16 9.5l0.147 3h5.1l0.182 -2.82Z"
            strokeWidth={1}
        />
        <path
            fill="#ffffff"
            d="M11.845 5a4.5 4.5 0 0 0 -4.793 3.808A3.53 3.53 0 0 0 5.5 8.5a4 4 0 0 0 -4 4h1.124A3.983 3.983 0 0 1 5.5 11.268a3.531 3.531 0 0 1 1.552 0.312 4.5 4.5 0 0 1 8.948 0.688l0.011 0.231h0.136L16 9.5A4.5 4.5 0 0 0 11.845 5Z"
            strokeWidth={1}
        />
        <path
            fill="#ffffff"
            d="m16.147 12.501 5.103 -0.001 -5.103 -0.001v0.002Z"
            strokeWidth={1}
        />
        <path
            fill="#ffffff"
            d="m21.429 9.68 -0.179 2.82h0.176v-0.048c0.034 0.013 0.065 0.033 0.1 0.048H23V12a2.5 2.5 0 0 0 -1.571 -2.32Z"
            strokeWidth={1}
        />
        <path
            fill="#ffbc44"
            d="m16.5 20.94 0.733 1.876a0.5 0.5 0 0 1 -0.465 0.682H7.232a0.5 0.5 0 0 1 -0.466 -0.682L7.5 20.94"
            strokeWidth={1}
        />
        <path
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m16.5 20.94 0.733 1.876a0.5 0.5 0 0 1 -0.465 0.682H7.232a0.5 0.5 0 0 1 -0.466 -0.682L7.5 20.94"
            strokeWidth={1}
        />
        <path
            fill="#fff9bf"
            d="M22.982 12.5a0.507 0.507 0 0 1 0.5 0.521C23.2 18.743 18.167 21.5 12 21.5 5.833 21.5 0.8 18.743 0.513 13.021a0.506 0.506 0 0 1 0.505 -0.521h21.964Z"
            strokeWidth={1}
        />
        <path
            fill="#ffef5e"
            d="M22.982 12.5h-0.2C21.16 16.157 16.948 17.937 12 17.937S2.841 16.157 1.218 12.5h-0.2a0.507 0.507 0 0 0 -0.5 0.521C0.8 18.743 5.833 21.5 12 21.5c6.167 0 11.2 -2.757 11.487 -8.479a0.506 0.506 0 0 0 -0.5 -0.521h-0.005Z"
            strokeWidth={1}
        />
        <path
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M22.982 12.5a0.507 0.507 0 0 1 0.5 0.521C23.2 18.743 18.167 21.5 12 21.5 5.833 21.5 0.8 18.743 0.513 13.021a0.506 0.506 0 0 1 0.505 -0.521h21.964Z"
            strokeWidth={1}
        />
        <path
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m20 0.5 -2.5 10"
            strokeWidth={1}
        />
        <path
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m23.5 0.5 -4.5 10"
            strokeWidth={1}
        />
        <path
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21.429 9.68A2.5 2.5 0 0 1 23 12v0.5H1.5a4 4 0 0 1 4 -4 3.531 3.531 0 0 1 1.552 0.312A4.5 4.5 0 0 1 16 9.5"
            strokeWidth={1}
        />
    </svg>
);
const Banana = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        id="Fruit-Banana-1--Streamline-Ultimate"
        height={24}
        width={24}
    >
        <desc>
            {"\n    Fruit Banana 1 Streamline Icon: https://streamlinehq.com\n  "}
        </desc>
        <path
            fill="#ffef5e"
            d="M23.053 10.932c-0.0019 3.3335 -1.3273 6.5299 -3.6849 8.8866 -2.3576 2.3567 -5.5546 3.6809 -8.8881 3.6814 -4.724 0 -6.016 -1.994 -8.648 -2.967 -0.06245 -0.0224 -0.11981 -0.0571 -0.16874 -0.1019 -0.04893 -0.0448 -0.08845 -0.0989 -0.11626 -0.1591 -0.02784 -0.06 -0.0435 -0.1249 -0.04607 -0.191 -0.00258 -0.0661 0.00798 -0.132 0.03107 -0.194l0.349 -0.943c0.02231 -0.0624 0.0568 -0.1198 0.10148 -0.1687 0.04467 -0.049 0.09863 -0.0886 0.15874 -0.1165 0.06011 -0.0279 0.12518 -0.0436 0.1914 -0.0461 0.06623 -0.0026 0.1323 0.0081 0.19438 0.0313 7.328 2.709 18.963 -6.806 14.09 -16.55401 -0.0698 -0.14107 -0.1055 -0.29654 -0.1043 -0.45391 0.0012 -0.15738 0.0394 -0.31228 0.1114 -0.45224 0.0719 -0.13996 0.1757 -0.261079 0.303 -0.353624 0.1273 -0.092546 0.2746 -0.153934 0.4299 -0.17923l0.823 -0.137c0.2363 -0.040476 0.4794 0.005318 0.6848 0.129037 0.2054 0.123718 0.3595 0.317122 0.4342 0.544967 1.244 3.721 3.754 3.933 3.754 9.74401Z"
            strokeWidth={1}
        />
        <path
            fill="#fff9bf"
            d="M7.867 21.037c3.3337 -0.0005 6.5308 -1.3248 8.8885 -3.6817 2.3576 -2.3569 3.6829 -5.5536 3.6845 -8.8873 0 -4.64 -1.6 -5.713 -2.878 -7.850996l-0.205 0.034c-0.1553 0.025296 -0.3026 0.086684 -0.4299 0.17923 -0.1273 0.092545 -0.2311 0.213666 -0.303 0.353626 -0.072 0.13996 -0.1102 0.29486 -0.1114 0.45223 -0.0012 0.15738 0.0345 0.31285 0.1043 0.45391 4.873 9.748 -6.762 19.263 -14.09 16.555 -0.06208 -0.0232 -0.12815 -0.0339 -0.19438 -0.0313 -0.06622 0.0025 -0.13128 0.0182 -0.1914 0.0461 -0.06011 0.0279 -0.11407 0.0675 -0.15874 0.1165 -0.04468 0.0489 -0.07917 0.1063 -0.10148 0.1687l-0.153 0.412c1.83404 1.1573 3.97122 1.7422 6.139 1.68Z"
            strokeWidth={1}
        />
        <path
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M23.053 10.932c-0.0019 3.3335 -1.3273 6.5299 -3.6849 8.8866 -2.3576 2.3567 -5.5546 3.6809 -8.8881 3.6814 -4.724 0 -6.016 -1.994 -8.648 -2.967 -0.06245 -0.0224 -0.11981 -0.0571 -0.16874 -0.1019 -0.04893 -0.0448 -0.08845 -0.0989 -0.11626 -0.1591 -0.02784 -0.06 -0.0435 -0.1249 -0.04607 -0.191 -0.00258 -0.0661 0.00798 -0.132 0.03107 -0.194l0.349 -0.943c0.02231 -0.0624 0.0568 -0.1198 0.10148 -0.1687 0.04467 -0.049 0.09863 -0.0886 0.15874 -0.1165 0.06011 -0.0279 0.12518 -0.0436 0.1914 -0.0461 0.06623 -0.0026 0.1323 0.0081 0.19438 0.0313 7.328 2.709 18.963 -6.806 14.09 -16.55401 -0.0698 -0.14107 -0.1055 -0.29654 -0.1043 -0.45391 0.0012 -0.15738 0.0394 -0.31228 0.1114 -0.45224 0.0719 -0.13996 0.1757 -0.261079 0.303 -0.353624 0.1273 -0.092546 0.2746 -0.153934 0.4299 -0.17923l0.823 -0.137c0.2363 -0.040476 0.4794 0.005318 0.6848 0.129037 0.2054 0.123718 0.3595 0.317122 0.4342 0.544967 1.244 3.721 3.754 3.933 3.754 9.74401Z"
            strokeWidth={1}
        />
        <path
            stroke="#191919"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.98 8.71002c-0.2999 3.11208 -1.6644 6.02538 -3.8632 8.24798 -2.1988 2.2227 -5.0972 3.6186 -8.2058 3.952"
            strokeWidth={1}
        />
    </svg>
);

const iconMapping: Record<string, (() => React.JSX.Element) | undefined> = {
    "common-399-banana": Banana,
    "common-7820-chicken-breast": Chicken,
    "common-1743-chicken-thigh": Chicken,
    "common-363-broccoli": Broccoli,
    "common-784-rice": Rice,
};

const MealEntryCard = (props: EntryCardProps) => {
    const {entry, provided, entryLink} = props;
    const useKilojoules = forms.useUseKilojoules();

    const resolved = resolveEntry(entry);

    const isLoading = resolved.kind !== "complete";
    const blockColor = resolved.kind === "error" ? "bg-red-100" : "bg-gray-200";
    const textColor = resolved.kind === "error" ? "text-red-600" : "text-gray-600";

    const calculations = getRoundedCalculations(
        calculateEntryMetrics({
            entry,
            quantity: entry.quantity,
            unit: entry.unit,
        }),
        useKilojoules,
    );

    const photoDialog = dialogs.useDialog<unknown, {image_uid: UUID}>({
        Content: (props) => {
            return (
                <img
                    alt={entry.name}
                    src={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${props.image_uid}/public`}
                />
            );
        },
    });

    const quantityAndUnitDisplay = {
        quantity: getRoundedQuantity(entry.quantity),
        unit: entry.unit,
    };

    const photo = entry.nutritional_vision_prompt?.image?.image_uid;
    const Icon = iconMapping[entry.backend_id];

    return (
        <TouchHandler
            className="relative flex flex-col gap-0 px-3 py-3.5 pl-8"
            haptic
            link={entryLink}
            role="link"
            ariaLabel={`Update ${entry.name}`}
        >
            {photoDialog.dialog}
            <div
                id={entry.uuid}
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                className="flex items-center"
            >
                {Icon != null && (
                    <div className="-ml-6 pr-4">
                        <div
                            className="
                                flex size-8 items-center justify-center rounded-full
                                bg-indigo-100
                                *:size-4.5
                            "
                        >
                            <Icon />
                        </div>
                    </div>
                )}

                {photo != null && (
                    <div className="mr-4 -ml-4 flex justify-center">
                        <button
                            type="button"
                            className="size-5"
                            onClick={(event) => {
                                console.log("clicked");
                                event.preventDefault();
                                event.stopPropagation();
                                void photoDialog({image_uid: photo});
                            }}
                        >
                            <PhotoIcon className="size-5" />
                        </button>
                    </div>
                )}
                <motion.div
                    className="flex h-10 min-w-0 flex-1 flex-col gap-1 py-0"
                    animate={isLoading ? {scale: [1, 1.02, 1]} : {scale: 1}}
                    transition={{duration: 0.3, ease: "easeInOut"}}
                >
                    <div className="-ml-1 flex items-center gap-4">
                        <AnimatePresence mode="wait" initial={false}>
                            {resolved.kind === "error" ? (
                                <motion.div
                                    key="error"
                                    initial={{opacity: 0, x: -10}}
                                    animate={{opacity: 1, x: 0}}
                                    exit={{opacity: 0, x: 10}}
                                    transition={{duration: 0.2}}
                                    className={`
                                        flex-1 text-sm font-medium
                                        ${textColor}
                                    `}
                                >
                                    Error processing
                                </motion.div>
                            ) : resolved.kind === "processing" ? (
                                <motion.div
                                    key="processing"
                                    initial={{opacity: 0, x: -10}}
                                    animate={{opacity: 1, x: 0}}
                                    exit={{opacity: 0, x: 10}}
                                    transition={{duration: 0.2}}
                                    className={`
                                        flex-1 truncate text-sm font-medium
                                        ${textColor}
                                    `}
                                >
                                    {resolved.type === "AUDIO"
                                        ? "Analyzing audio..."
                                        : "Analyzing image..."}
                                </motion.div>
                            ) : resolved.kind === "identified" ? (
                                <motion.div
                                    key="identified"
                                    initial={{opacity: 0, x: -10}}
                                    animate={{opacity: 1, x: 0}}
                                    exit={{opacity: 0, x: 10}}
                                    transition={{duration: 0.2}}
                                    className={`
                                        flex-1 truncate text-sm font-medium
                                        ${textColor}
                                    `}
                                >
                                    {resolved.name}
                                </motion.div>
                            ) : (
                                <motion.h4
                                    key="title"
                                    initial={{opacity: 0, x: -10}}
                                    animate={{opacity: 1, x: 0}}
                                    exit={{opacity: 0, x: 10}}
                                    transition={{duration: 0.2}}
                                    className="flex-1 truncate text-sm font-medium"
                                >
                                    {entry.name}
                                </motion.h4>
                            )}
                        </AnimatePresence>

                        <AnimatePresence mode="wait" initial={false}>
                            {isLoading ? (
                                <motion.div
                                    key="skeleton-cal"
                                    initial={{opacity: 0, scale: 0.8}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.8}}
                                    transition={{duration: 0.2}}
                                    className={`
                                        h-4 w-12 rounded-sm
                                        ${blockColor}
                                        animate-pulse
                                    `}
                                />
                            ) : (
                                <motion.div
                                    key="calories"
                                    initial={{opacity: 0, scale: 0.8}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.8}}
                                    transition={{duration: 0.2}}
                                    className="text-xs text-gray-700"
                                >
                                    <span className="font-medium">
                                        {calculations.calories}
                                    </span>{" "}
                                    <span className="font-normal text-gray-500">
                                        {useKilojoules ? "kj" : "cal"}
                                    </span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <div className="-ml-1 flex items-center gap-4">
                        <AnimatePresence mode="wait" initial={false}>
                            {isLoading ? (
                                <motion.div
                                    key="skeleton-macros"
                                    initial={{opacity: 0, scale: 0.8}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.8}}
                                    transition={{duration: 0.2}}
                                    className="mt-0.5 flex w-42 gap-1.5"
                                >
                                    {[0, 1, 2].map((i) => (
                                        <motion.div
                                            key={i}
                                            initial={{opacity: 0, scale: 0.8}}
                                            animate={{opacity: 1, scale: 1}}
                                            transition={{
                                                duration: 0.2,
                                                delay: i * 0.05,
                                            }}
                                            className={`
                                                h-3 w-10 rounded-sm
                                                ${blockColor}
                                                animate-pulse
                                            `}
                                        />
                                    ))}
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="macros"
                                    initial={{opacity: 0, scale: 0.8}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.8}}
                                    transition={{duration: 0.2}}
                                    className="w-42"
                                >
                                    <EntryMetrics calculations={calculations} />
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <AnimatePresence mode="wait" initial={false}>
                            {isLoading ? (
                                <motion.div
                                    key="skeleton-quantity"
                                    initial={{opacity: 0, scale: 0.8}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.8}}
                                    transition={{duration: 0.2}}
                                    className="flex-1 text-right"
                                >
                                    <div
                                        className={`
                                            h-3 w-16 rounded-sm
                                            ${blockColor}
                                            ml-auto animate-pulse
                                        `}
                                    />
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="quantity"
                                    initial={{opacity: 0, scale: 0.8}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.8}}
                                    transition={{duration: 0.2}}
                                    className="
                                        flex-1 truncate text-right text-xs font-medium
                                        text-gray-700
                                    "
                                >
                                    <span className={classNames(level2Class)}>
                                        {quantityAndUnitDisplay.quantity}{" "}
                                        {quantityAndUnitDisplay.unit}
                                    </span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            </div>
        </TouchHandler>
    );
};

interface CardProps {
    isExpanded: boolean;
    children: React.ReactNode;
    expandedContent?: React.ReactNode;
    rightAction?: React.ReactNode;
    caloriesDisplay?: {
        value: string;
        className: string;
    };
    dragHandle?: DraggableProvidedDragHandleProps | null;
}

const Card = (props: CardProps) => {
    const {
        isExpanded,
        children,
        expandedContent,
        rightAction,
        caloriesDisplay,
        dragHandle,
    } = props;

    return (
        <>
            <div
                {...dragHandle}
                className={classNames(
                    "flex items-center gap-2 pt-3 pb-2 px-2 mt-2.5 bg-white rounded-t-lg border-b",
                    isExpanded
                        ? "border-b border-gray-200"
                        : "rounded-b-lg border-white",
                )}
            >
                <div className="flex min-w-0 flex-1 items-center gap-4">{children}</div>
                {caloriesDisplay && (
                    <div
                        className={classNames(
                            "text-sm truncate",
                            caloriesDisplay.className,
                        )}
                    >
                        <span className="font-medium">{caloriesDisplay.value}</span>{" "}
                        <span className="font-light">cal</span>
                    </div>
                )}
                {rightAction}
            </div>
            {expandedContent !== undefined && (
                <motion.div
                    initial={isExpanded ? false : {height: 0, opacity: 0}}
                    animate={{
                        height: isExpanded ? "auto" : 0,
                        opacity: isExpanded ? 1 : 0,
                    }}
                    transition={{duration: 0.2, ease: "easeInOut"}}
                    style={{overflow: "hidden"}}
                >
                    {expandedContent}
                </motion.div>
            )}
        </>
    );
};

export const MealCard = (props: Props) => {
    const {links, meal, sorting, defaultExpanded} = props;
    const dispatch = useAppDispatch();
    const {manualMealExpansions, lastUpdatedMealUuid} = useAppSelector(
        (state) => state.search,
    );

    // Check if user has manually set expansion state for this meal
    const hasManualState = meal.uuid in manualMealExpansions;
    const isRecentlyUpdated = meal.uuid === lastUpdatedMealUuid;

    const isExpanded = hasManualState
        ? manualMealExpansions[meal.uuid]
        : isRecentlyUpdated
          ? true
          : (defaultExpanded ?? false); //  && sorting?.isDragging != true;
    const useKilojoules = forms.useUseKilojoules();

    const calculations = getRoundedCalculations(
        calculateMealMetrics(meal),
        useKilojoules,
    );

    const rendered = (
        <div {...props.sorting?.handle} className="rounded-b-xl bg-white">
            <Droppable
                droppableId={meal.uuid.toString()}
                type={constants.DROPPABLE_TYPES.ENTRY}
            >
                {(provided) => (
                    <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="divide-y divide-gray-200"
                    >
                        {meal.entries.map((entry, index) => (
                            <Draggable
                                key={entry.uuid}
                                draggableId={entry.uuid}
                                index={index}
                            >
                                {(provided) => (
                                    <MealEntryCard
                                        entry={entry}
                                        index={index}
                                        provided={provided}
                                        entryLink={links?.getEntryLink(entry)}
                                    />
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </div>
    );

    const {SortContext, SortProps} =
        sorting == null
            ? {SortContext: DragDropContext, SortProps: {onDragEnd: () => {}}}
            : {SortContext: React.Fragment, SortProps: {} as {onDragEnd: () => void}};

    return (
        <SortContext {...SortProps}>
            <Card
                isExpanded={isExpanded}
                dragHandle={sorting?.handle}
                caloriesDisplay={
                    calculations.calories != null
                        ? {
                              value: calculations.calories,
                              className: "text-gray-700",
                          }
                        : undefined
                }
                rightAction={
                    <button
                        className="
                            flex size-7 items-center justify-center rounded-full
                            bg-stone-200/40 p-1.5
                        "
                        aria-label={isExpanded ? "Collapse meal" : "Expand meal"}
                        type="button"
                        onClick={() => {
                            dispatch({
                                type: isExpanded ? "COLLAPSE_MEAL" : "EXPAND_MEAL",
                                mealUuid: meal.uuid,
                            });
                            hardware.haptic();
                        }}
                    >
                        <motion.svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="size-4"
                            initial={{rotate: 180}}
                            animate={{rotate: isExpanded ? 0 : 180}}
                            transition={{duration: 0.2, ease: "easeInOut"}}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m4.5 15.75 7.5-7.5 7.5 7.5"
                            />
                        </motion.svg>
                    </button>
                }
                expandedContent={rendered}
            >
                <TouchHandler
                    className={classNames(
                        "text-center font-normal bg-stone-200/40 px-3 h-9 rounded-full flex flex-col items-center justify-center min-w-[4.2rem]",
                        meal.type == "AUTO_CREATED" ? "text-sm" : "text-xs",
                    )}
                    link={links?.mealLink}
                    haptic
                    role="button"
                >
                    {meal.type == "AUTO_CREATED" && meal.hour != null ? (
                        <span className="whitespace-nowrap">
                            {hourToName(meal.hour)}
                            {meal.hour == LATE_MEAL_HOUR && (
                                <sup
                                    aria-label="next day"
                                    className="
                                        ml-px text-[10px] font-semibold text-stone-500
                                    "
                                >
                                    +1
                                </sup>
                            )}
                        </span>
                    ) : (
                        meal.name
                    )}
                </TouchHandler>
                {(meal.type == "AUTO_CREATED" || meal.name.length < 12) && (
                    <div className="w-40">
                        <EntryMetrics calculations={calculations} />
                    </div>
                )}
            </Card>
        </SortContext>
    );
};

export function ActivityCard({activity}: {activity: ActivityRow}) {
    const isExpanded = false as boolean;
    const rendered = null;
    const durationMinutes = Math.round(activity.activity.duration_seconds / 60);

    return (
        <Card
            isExpanded={isExpanded}
            caloriesDisplay={
                activity.activity.calories != null
                    ? {
                          value: new Decimal(activity.activity.calories)
                              .toDecimalPlaces(0)
                              .toString(),
                          className: "text-red-900",
                      }
                    : undefined
            }
            rightAction={
                <span
                    className="
                        flex size-7 items-center justify-center rounded-full
                        bg-red-200/40 p-1.5 text-red-800
                    "
                >
                    <Icon icon="bolt" />
                </span>
            }
            expandedContent={rendered}
        >
            <TouchHandler
                className={classNames(
                    "text-center font-normal bg-red-200/40 px-3 h-9 rounded-full flex flex-col items-center justify-center min-w-[4.2rem] text-red-900",
                    "text-sm",
                )}
                haptic
                role="button"
            >
                {activity.hour}
            </TouchHandler>
            <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-base/5">{activity.name}</span>
                <span className="text-xs font-normal text-gray-500">
                    {durationMinutes} min
                </span>
            </div>
        </Card>
    );
}
